import ENV from "config/Env";
import { typedQueryClient, useTypedMutation } from "hooks";
import { LANDING_PAGES, LANGUAGE_CODES, USER_STATUSES } from "models";
import { useBrandGroupsQuery } from "modules/BrandsDashboard/hooks";
import { getBrandGroupsByIds } from "modules/BrandsDashboard/utils";
import { UsersDashboardQueryKeys } from "modules/UsersDashboard/queryKeys";
import { ApiInstance } from "utils/Api";

import type { BrandGroup, User } from "models";
import type { CreateEditUserForm } from "modules/UsersDashboard/components";

type ConstructPayloadParams = {
  params: CreateEditUserForm;
  brandGroups: BrandGroup[];
};

type ConstructNewUser = {
  params: CreateEditUserForm;
  brandGroups: BrandGroup[];
  id: number;
};

const NEW_USER_DEFAULTS = {
  created_on: "",
  updated_on: "",
  mobile: "",
  address1: "",
  address2: "",
  web_version: "",
  app_version: "",
  camera_version: "",
  profile_picture: "",
  language_code: LANGUAGE_CODES.English,
  landing_page_id: LANDING_PAGES.Main,
  location_column_visible: false,
  list_appointments_by_scheduled_range: false,
  is_available: false,
  pin_status_notification_enabled: false,
  customer_waiting_car_in_shop_notification_enabled: false,
  shop_status_same_day_filter_enabled: false,
  shop_status_next_day_filter_enabled: false,
  shop_status_to_order_filter_enabled: false,
  shop_status_backorder_filter_enabled: false,
  shop_status_arrived_parts_filter_enabled: false,
  star_color_green_enabled: false,
  star_color_red_enabled: false,
  star_color_blue_enabled: false,
  star_color_yellow_enabled: false,
  working_on: null,
  day_planner_appointments_collapsed_by_default: false,
  day_planner_cars_ready_collapsed_by_default: false,
  day_planner_quality_control_collapsed_by_default: false,
  car_type_filter: null,
  is_vat_enabled_on_wo: false,
  is_desktop_notifications_enabled: false
};

const constructPayload = ({ params, brandGroups }: ConstructPayloadParams) => {
  const { isActive, confirmPassword: _confirmPassword, dms_nr, ...rest } = params;

  return {
    ...rest,
    status: isActive ? USER_STATUSES.UserActive : USER_STATUSES.UserInactive,
    brand_groups: getBrandGroupsByIds(rest.brand_group_ids, brandGroups),
    dms_nr: dms_nr ? String(dms_nr) : null
  };
};

const constructNewUser = ({ id, params: { password: _password, confirmPassword: _confirmPassword, ...params }, brandGroups }: ConstructNewUser): User => ({
  ...NEW_USER_DEFAULTS,
  ...params,
  id,
  status: params.isActive ? USER_STATUSES.UserActive : USER_STATUSES.UserInactive,
  brand_groups: getBrandGroupsByIds(params.brand_group_ids, brandGroups),
  phone: params.phone ?? "",
  role_id: params.role_id ?? 0,
  dealer_id: params.dealer_id ?? -1,
  dealer_location_id: params.dealer_location_id ?? 0,
  dealers_ids: params.dealers_ids ?? [],
  location_ids: params.location_ids ?? [],
  is_tablet_translation_ai_enabled: params.is_tablet_translation_ai_enabled ?? false,
  onei_planning_monteur_id: params.onei_planning_monteur_id ?? undefined,
  planit_planning_mechanic_id: params.planit_planning_mechanic_id ?? undefined
});

export const useCreateUserMutation = () => {
  const { data: brandGroups = [] } = useBrandGroupsQuery();

  return useTypedMutation<CreateEditUserForm, number>({
    mutationFn: async params => (await ApiInstance.post(`/users/create`, constructPayload({ params, brandGroups }), ENV.systemBaseURL)).data.user_id,
    onSuccess: (payload, id) => {
      const previousUsers = typedQueryClient.getQueryData(UsersDashboardQueryKeys.users);

      typedQueryClient.setQueryData(UsersDashboardQueryKeys.users, {
        ...previousUsers,
        users: [constructNewUser({ params: payload, id, brandGroups }), ...(previousUsers?.users ?? [])],
        nb_items: (previousUsers?.nb_items ?? 0) + 1
      });
    }
  });
};
